<template>
  <div>

    <!-- File inputs -->
    <template>
      <input
        v-show="false"
        type="file"
        ref="audio"
        multiple
        accept="audio/mp3"
        @change="fileChanged('audio')"
      />

      <input
        v-show="false"
        type="file"
        ref="image"
        accept="image/*"
        multiple
        @change="fileChanged('image')"
      />

      <!-- <input
        v-show="false"
        type="file"
        ref="document"
        multiple
        @change="fileChanged('document')"
      /> -->

      <input
        v-show="false"
        type="file"
        ref="video"
        multiple
        accept="video/mp4"
        @change="fileChanged('video')"
      />
    </template>

    <v-dialog v-model="visible" max-width="500px" persistent>
      <v-card>
        
        <!-- Toolbar -->
        <v-toolbar :color="toolbarColor" dense dark flat>

          <v-btn fab small @click="cancel" class="mr-3 elevation-0" :color="toolbarColorDark" :loading="isSaving">
            <v-icon>close</v-icon>
          </v-btn>

          <v-toolbar-title class="ml-3">{{ getExpression('FormTitle', 'MediaProperties') }}</v-toolbar-title>

          <v-spacer />

          <v-toolbar-items>
            <v-btn
              @click="submit"
              large
              :color="toolbarColorDark"
              class="elevation-0"
              :loading="isSaving"
              >
              <span class="subheading font-weight-medium">{{ submitButtonText }}</span>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-card-text>
      
          <!-- Data Iterator -->
          <v-data-iterator
            :items="blobProperties"
            :items-per-page.sync="itemsPerPage"
            :page="page"
            hide-default-footer
            >
            
            <!-- Properties -->
            <template v-slot:default="props">
              <div
                d-flex
                v-for="item in props.items"
                :key="item.blobName"
                >
                <v-list v-if="item !== null" dense two-line>

                  <media-properties-form v-bind:blob="item" class="mb-3" />
                  
                  <template>
                    <v-text-field
                      :label="getExpression('Label', 'Title')"
                      v-model="item.title"
                      maxlength="100"
                      :disabled="item.isLibraryItem"
                      v-validate="'required|max:100'"
                      :error-messages="errors.collect('title')"
                      data-vv-name="title"
                      ref="title"
                      clearable
                      >
                    </v-text-field>
            
                    <template v-if="isGenie">
                      <v-text-field
                        :label="getExpression('Label', 'Description')"
                        v-model="item.description"
                        maxlength="200"
                        :disabled="item.isLibraryItem"
                        v-validate="isGenie ? 'max:200' : ''"
                        :error-messages="errors.collect('description')"
                        data-vv-name="description"
                        clearable
                        >
                      </v-text-field>
            
                      <v-select
                        v-if="isKiosk"
                        v-model="item.kioskFolder"
                        :items="kioskFolders"
                        item-value="value"
                        item-text="text"
                        :label="getExpression('Label', 'AmbientBehaviour')"
                        :hint="getBehaviourHint(item)"
                        persistent-hint
                        >
                      </v-select>
                    </template>
                  </template>
                </v-list>
              </div>
            </template>

            <!-- Footer -->
            <template v-slot:footer v-if="numberOfPages > 1">
              <v-row class="mr-2 pb-2" justify="end">
                <span class="mr-4 grey--text subheading">
                  File {{ page }} of {{ numberOfPages }}
                </span>
                <v-btn
                  fab
                  small
                  dark
                  :color="toolbarColorDark"
                  class="mr-1"
                  @click="formerPage"
                  >
                  <v-icon>keyboard_arrow_left</v-icon>
                </v-btn>
                <v-btn
                  fab
                  small
                  dark
                  :color="toolbarColorDark"
                  class="ml-1"
                  @click="nextPage"
                  >
                  <v-icon>keyboard_arrow_right</v-icon>
                </v-btn>
              </v-row>
            </template>

          </v-data-iterator>

        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import appService from '@/app.service'
import { AppColor, Context, KioskFolder, DestinationId, ImageUri } from '@/config.js'
import Media from '@/plugins/media.js'
import { mapGetters } from 'vuex'
import MediaPropertiesForm from '@/components/MediaPropertiesForm'

// genie only
const ALLOWED_AUDIO_TYPES = ['audio/mp3', 'audio/mp4', 'audio/mpeg']
const ALLOWED_VIDEO_TYPES = ['video/mp4']

// usage modes
const ADD_MODE = 1
const EDIT_MODE = 2
const DELETE_MODE = 3

export default {
  name: "AppMedia",
  components: {
    MediaPropertiesForm
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      // data iterator
      page: 1,
      itemsPerPage: 1,
      itemsPerPageArray: [1, 4, 12],

      // contexts
      context_Genie: Context.Genie,
      context_Abby: Context.Abby,

      // kiosk folders
      kioskFolder_Ambient: KioskFolder.Ambient,
      kioskFolder_Announcement: KioskFolder.Announcement,

      // destinations
      destinationId_MediaLibrary: DestinationId.MediaLibrary,

      // image uri
      imageUri_AudioVideoPlaceholder: ImageUri.AudioVideoPlaceholder,

      // so that the edited title isn't bound to the display title
      itemTitle: null,

      visible: false,
      submitButtonText: null,
      isSaving: false,
      usageMode: null,

      // file upload
      files: [],
      mediaFiles: [],

      // params
      context: null,
      contentType: null,
      blob: null,
      blobProperties: [],
      kioskId: null,
      container: null,
      deleteIds: null,
      toolbarColor: AppColor.Light,
      toolbarColorDark: AppColor.Medium,

      // genie
      playlistId: null,

      // abby
      profileId: null,
      mediaPathTypeId: null,
      allowedTypes: null,

      // callback
      onSubmit: {},
      onRemove: {},
      onCancel: {},
      onConfirm: {},
      onError: {}
    }
  },
  computed: {
    ...mapGetters(['profile', 'locale']),
    isGenie () {
      return this.context === this.context_Genie
    },
    isAbby () {
      return this.context === this.context_Abby
    },
    numberOfPages () {
      if (this.blobProperties === null) return 0
      return Math.ceil(this.blobProperties.length / this.itemsPerPage)
    },
    kioskFolders () {
      return [
        {
          value: 'ambient',
          text: this.getExpression('Label', 'Standard')
        },
        {
          value: 'announcement',
          text: this.getExpression('Label', 'Announcement')
        }
      ]
    },
    isKiosk () {
      if (this.isNullOrUndefined(this.kioskId)) return false 
      return this.kioskId > 0
    },
    dictionary () {
      return {
        custom: {
          title: {
            required: () => this.getExpression('Validation', 'TitleRequired'),
            max: this.getExpression('Validation', 'TitleMaxChars').replace('{0}', '100')
          },
          description: {
            max: this.getExpression('Validation', 'DescriptionMaxChars').replace('{0}', '200')
          }
        }
      }
    }
  },
  methods: {
    add (params) {
      this.reset(ADD_MODE)

      if (!this.initParams(params)) return

      this.submitButtonText = 'Upload'
      this.selectFiles()
    },

    edit (params) {
      this.reset(EDIT_MODE)

      if (!this.initParams(params)) return 

      // if a single blob is passed in, force it into an array
      if (this.blob !== null) {
        this.blobProperties = [this.blob]
      }

      this.submitButtonText = 'Save'
      this.visible = true
    },

    remove (params) {
      this.reset(DELETE_MODE)
      if (!this.initParams(params)) return
      this.deleteFiles()
    },

    initParams (params) {
      if (this.isNullOrUndefined(params)) return

      // validate required params
      var validParams = true
      switch (this.usageMode) {
        case ADD_MODE:
          validParams = !this.isNullOrUndefined(params.context) && !this.isNullOrUndefined(params.contentType)
          break
        case EDIT_MODE:
          validParams = !this.isNullOrUndefined(params.context)
          break
        case DELETE_MODE:
          validParams = !this.isNullOrUndefined(params.context) && !this.isNullOrUndefined(params.deleteIds)
          break
      }
      if (!validParams) {
        this.showUsageWarning()
        return false
      }

      this.context = params.context
      this.contentType = params.contentType

      // common
      if (!this.isNullOrUndefined(params.blob)) this.blob = params.blob
      if (!this.isNullOrUndefined(params.container)) this.container = params.container
      if (!this.isNullOrUndefined(params.kioskId)) this.kioskId = params.kioskId
      if (!this.isNullOrUndefined(params.deleteIds)) this.deleteIds = params.deleteIds
      if (!this.isNullOrUndefined(params.toolbarColor)) this.toolbarColor = params.toolbarColor
      if (!this.isNullOrUndefined(params.toolbarColorDark)) this.toolbarColorDark = params.toolbarColorDark
      
      // genie
      if (!this.isNullOrUndefined(params.playlistId)) this.playlistId = params.playlistId

      // abby
      if (!this.isNullOrUndefined(params.mediaPathTypeId)) this.mediaPathTypeId = params.mediaPathTypeId
      if (!this.isNullOrUndefined(params.allowedTypes)) this.allowedTypes = params.allowedTypes
      if (!this.isNullOrUndefined(params.profileId)) this.profileId = params.profileId

      // callback
      if (!this.isNullOrUndefined(params.onSubmit)) this.onSubmit = params.onSubmit
      if (!this.isNullOrUndefined(params.onCancel)) this.onCancel = params.onCancel
      if (!this.isNullOrUndefined(params.onRemove)) this.onRemove = params.onRemove
      if (!this.isNullOrUndefined(params.onConfirm)) this.onConfirm = params.onConfirm
      if (!this.isNullOrUndefined(params.onError)) this.onError = params.onError

      return true
    },

    reset (mode) {
      this.usageMode = mode
      this.context = null
      this.contentType = null
      this.blob = null
      this.blobProperties = []
      this.container = null
      this.kioskId = null
      this.deleteIds = null
      this.toolbarColor = AppColor.Medium
      this.toolbarColorDark = AppColor.Dark
      this.playlistId = null
      this.mediaPathTypeId = null
      this.allowedTypes = null
      this.profileId = null
      this.deleteIds = null
      this.onSubmit = {}
      this.onCancel = {}
      this.onRemove = {}
      this.onConfirm = {}
      this.onError = {}

      this.$validator.reset()
      this.$validator.pause()
    },

    selectFiles () {
      if (this.contentType === null) return
      switch (this.contentType) {
        case 'audio':
          this.$refs.audio.click()
          break
        case 'image':
          //if (this.isGenie) {
          //  this.$refs.document.click()
          //} else if (this.isAbby) {
            this.$refs.image.click()
          //}
          break
        case 'video':
          if (this.isAbby) {
            this.$refs.video.setAttribute('accept', 'video/*')
          } else {
            this.$refs.video.setAttribute('accept', 'video/mp4')
          }
          this.$refs.video.click()
          break
      }
    },

    fileChanged (ref) {
      const files = this.$refs[ref].files
      // a document is either an image or something intended to be one (convert api will handle it)
      //const fileRef = ref === 'document' ? 'image' : ref
      const fileRef = ref
      const maxFiles = files.length

      this.mediaFiles = []
      this.blobProperties = []

      for (var idx = 0; idx < maxFiles; idx++) {
        const file = files[idx]
        var previewUrl = ''
        const mediaType = this.getMediaType(file.type)

        var allowedTypes = []    
        if (this.allowedTypes !== null) {
          allowedTypes = this.allowedTypes.split(',')
        } else {
          switch (fileRef) {
            case 'audio':
              allowedTypes = ALLOWED_AUDIO_TYPES
              break
            case 'video':
              allowedTypes = ALLOWED_VIDEO_TYPES
              break
          }
        }
        //-- image rules
        if (fileRef === 'image' && mediaType === 'image' 
          && file.type.includes('image/')) {
                  
          previewUrl = URL.createObjectURL(file)

        //-- video rules
        } else if (fileRef === 'video' && mediaType === 'video'
              && ((this.isGenie && allowedTypes.includes(file.type))
                || (this.isAbby && file.type.includes('video/')))) {

          previewUrl = this.imageUri_AudioVideoPlaceholder

        //-- audio rules
        } else if (fileRef === 'audio' && mediaType === 'audio'
          && allowedTypes.includes(file.type)) {

          previewUrl = this.imageUri_AudioVideoPlaceholder
        } else {
          continue
        }

        this.mediaFiles.push(file)
        this.blobProperties.push({
          blobName: file.name,
          contentType: file.type,
          size: file.size,
          previewUrl: previewUrl,
          title: this.getDefaultTitle(file.name),
          description: null,
          kioskFolder: this.isKiosk
            ? this.kioskFolder_Ambient
            : null
        })
      }

      if (this.blobProperties.length > 0) {
        this.$refs[ref].value = null
        this.visible = true
      } else {
        var text = ''
        const languagedContentType = this.getLanguagedContentType(fileRef)

        if (fileRef === 'image') {
          text = this.getExpression('Alert', 'SelectValidFiles').replace('{0}', languagedContentType)
        } else if (ref === 'video') {
          text = this.getExpression('Alert', 'SelectValidFiles').replace('{0}', languagedContentType)
        } else if (ref === 'audio') {
          text = this.getExpression('Alert', 'OnlyMp3Supported')
        }
        this.$error.show({
          title: this.getExpression('FormTitle', 'InvalidMedia'),
          severity: 0,
          text: text
        })
      }
    },

    submit () {
      if (this.usageMode === ADD_MODE) {
        this.upload()
      } else {
        this.save()
      }
    },

    upload () {
      const self = this
      const files = self.mediaFiles
      const fileCount = files.length

      let formData = new FormData()

      // file(s)
      files.forEach(file => {
        formData.append('file', file)
      })

      // metadata
      var metadata = null      
      if (self.isGenie) {
        metadata = self.blobProperties.map(obj => {
          return {
            blobName: obj.blobName,
            title: obj.title,
            description: obj.description,
            kioskFolder: obj.kioskFolder,
            destination: self.destinationId_MediaLibrary
          }
        })

      } else if (self.isAbby) {
        metadata = self.blobProperties.map(obj => {
          return {
            filename: obj.blobName,
            title: obj.title
          }
        })
      }

      formData.append('metadata', JSON.stringify(metadata))
      formData.append('containerName', self.container)
      formData.append('userId', self.profile.id)
      formData.append('deviceInfo', 'Admin Site')

      // kioskId
      if (self.kioskId > 0) {
        formData.append('kioskId', self.kioskId)
      }

      // genie playlist
      if (self.playlistId > 0) {
        formData.append('playlistId', self.playlistId)
      }

      // abby path type
      if (self.mediaPathTypeId > 0) {
        formData.append('mediaPathTypeId', self.mediaPathTypeId)
      }

      // abby profile
      if (self.profileId > 0) {
        formData.append('profileId', self.profileId)
      }

      // resource
      var resource = null
      if (self.isGenie) resource = 'mediaitems'
      else if (self.isAbby) resource = 'mediafiles'

      // upload route
      var uploadRoute = ''

      if (self.isGenie) {
        if (self.contentType === 'video') uploadRoute = '/videos'
        else if (self.contentType === 'audio') uploadRoute = '/audio'
        else uploadRoute = '/images'
      
      } else if (self.isAbby && self.contentType === 'video') {
        uploadRoute = self.contentType = '/videos'
      }

      self.$upload.execute({
        formData: formData,
        url: `${resource}/upload${uploadRoute}`,
        fileCount: fileCount,
        onComplete: (data) => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            const uploadResults = data.uploadResults
            if (typeof this.onSubmit === 'function') {
              this.visible = false
              this.onSubmit(uploadResults)
            }
          } 
        }
      })
    },

    save () {
      this.$validator.resume()
      this.$validator.validateAll().then(valid => {
        if (valid) {
          if (this.context === this.context_Genie) {
            this.saveGenie()
          } else if (this.context === this.context_Abby) {
            this.saveAbby()
          }
        } else {
          this.$validator.pause()
        }
      })
    },

    saveGenie () {
      const self = this
      const model = self.getModelGenie(self.blob)

      self.isSaving = true
      appService.updateMediaItem(model, self.container).then(data => {
        if (data.errorSeverity !== null) {
          self.handleError(data)
        } else {
          if (typeof this.onSubmit === 'function') {
            var mediaItem = data.mediaItem
            mediaItem.kioskFolder = self.blob.kioskFolder
            self.onSubmit(mediaItem)
            self.visible = false
          }
        }
        self.isSaving = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    getModelGenie (m) {
      return {
        mediaItemId: m.mediaItemId,
        blobName: m.blobName,
        title: m.title,
        description: m.description,
        kioskFolder: m.kioskFolder,
        contentType: m.contentType
      }
    },

    saveAbby () {
      const self = this
      const model = self.getModelAbby(self.blob)

      self.isSaving = true
      appService.updateAbbyMediaFile(model.mediaFileId, model).then((data) => {
        if (data.errorSeverity !== null) {
          self.handleError(data)
        } else {
          self.onSubmit(model)
          self.visible = false
        }
        self.isSaving = false
      }).catch(e => { self.handleCriticalError(e) })
    },
    
    getModelAbby (m) {
      return {
        mediaFileId: m.mediaFileId,
        title: m.title
      }
    },

    deleteFiles () {
      if (this.context === this.context_Genie) {
        this.deleteGenie()
      } else if (this.context === this.context_Abby) {
        this.deleteAbby()
      }
    },
    
    deleteGenie () {
      const self = this
      const ids = self.deleteIds

      self.$confirm.show({
        title: self.getExpression('FormTitle', 'DeleteMedia'),
        text: self.getExpression('Alert', 'DeleteMediaWarning').replace('{0}', ids.length),
        onConfirm: () => {
          if (typeof self.onConfirm === 'function') { self.onConfirm() }

          appService.deleteMediaItemsBulk(ids).then(data => {
            if (data.errorSeverity !== null) {
              if (typeof self.onError === 'function') {
                self.onError(data)
              }
            } else {
              if (typeof self.onRemove === 'function') {
                self.onRemove(data.mediaItems)
              }

            }
          }).catch(e => { 
            self.handleCriticalError(e)
          })
        }
      })
    },

    deleteAbby () {
      const self = this
      const ids = self.deleteIds

      self.$confirm.show({
        title: 'Delete Media?',
        text: `You have <b>${ids.length}</b> items selected.`,
        onConfirm: () => {
          if (typeof self.onConfirm === 'function') { self.onConfirm() }

          appService.deleteAbbyMediaFilesBulk(ids).then((data) => {
            if (data.errorSeverity !== null) {
              if (typeof self.onError === 'function') {
                self.onError(data)
              }
            } else {
              if (typeof self.onRemove === 'function') {
                self.onRemove(data.deletedIds)
              }
            }
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    cancel () {
      this.visible = false
      if (typeof this.onCancel === 'function') {
        this.onCancel()
      }
    },

    getBehaviourHint (item) {
      var hint = null

      var kioskFolder = item.kioskFolder !== null
        ? item.kioskFolder.toLowerCase()
        : ''

      if (kioskFolder === this.kioskFolder_Ambient)
        hint = this.getExpression('Alert', 'AmbientStandardHint')
      else if (kioskFolder === this.kioskFolder_Announcement)
        hint = this.getExpression('Alert', 'AmbientAnnouncementHint')

      return hint
    },

    nextPage () {
      if (this.page + 1 <= this.numberOfPages) this.page += 1
    },

    formerPage () {
      if (this.page - 1 >= 1) this.page -= 1
    },

    updateItemsPerPage (number) {
      this.itemsPerPage = number
    },
    
    showUsageWarning () {
      this.$error.show({
        title: 'AppMedia',
        text: 'Missing required params!',
        severity: 0
      })
    },

    handleError (result) {
      this.$error.show({
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  beforeMount() {
    Media.EventBus.$on('add', (params) => {
      this.add(params)
    })
    Media.EventBus.$on('edit', (params) => {
      this.edit(params)
    })
    Media.EventBus.$on('remove', (params) => {
      this.remove(params)
    })
  },
  mounted() {
    this.$validator.localize(this.locale, this.dictionary)
  }
}
</script>

<style>
.image {
  width: 60px;
  height: 60px;
}

.object-fit_contain {
  object-fit: contain;
  border: dotted 1px #bbb;
  margin-top: 3px;
}
</style>